// The inspector: click any number on a dashboard and it opens here. "Why" splits its change against the comparison
// across every driver (customer, item, category, location…), with the price / volume / mix bridge where there is
// one; click a driver to ask why again inside it. "Entries" lists the ledger rows that make up the number, each
// one a link into Business Central or the courageMFG record. Nothing on the page moves while you look.
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { api, type DSpec, type DTile, type NumberRef, type TileAction, type XDataset, type XDimension, type XExplanation, type XFilter, type XMember, type XQuery, type XResult } from "../api";
import { recordHref, StatePill } from "../ui";
import { ActPanel } from "./Act";
import { entriesQuery, explainRequest, type Pick } from "./inspect";
import { cellLink, memberLink } from "./links";
import { fmt, fmtCompact, fmtRange } from "./format";
import { KpiNumber } from "./tiles";

type Drill = { filter: XFilter; label: string; dim: string };
const signed = (v: number | null | undefined, format?: string) => {
  if (v === null || v === undefined || !Number.isFinite(v)) return "–";
  const s = v > 0 ? "+" : v < 0 ? "−" : "";
  const a = Math.abs(v);
  if (format === "pct") return `${s}${(a * 100).toFixed(1)} pts`;
  return `${s}${format === "money" ? "$" : ""}${fmtCompact(a, format === "money" ? "money" : format)}`;
};
const plain = (v: number | null | undefined, format?: string) => (v === null || v === undefined ? "–" : format === "money" ? `${v < 0 ? "−" : ""}$${fmtCompact(Math.abs(v), "money")}` : fmtCompact(v, format));
const CMP = { py: "prior year", pp: "prior period" } as const;

export function Inspector({ tile, main, spec, ds, pick, onClose, onCross, onWorkbook, dashboardId, dashboardName, actions = [], onActed }: {
  tile: DTile; main: XQuery; spec: DSpec; ds?: XDataset; pick: Pick | null;
  onClose: () => void; onCross?: (c: NonNullable<Pick["cross"]>) => void; onWorkbook?: (q: XQuery, title: string) => void;
  /** Acting on the number: the dashboard it's on, the records already raised from this tile, and what to do after. */
  dashboardId?: string; dashboardName?: string; actions?: TileAction[]; onActed?: (ref: NumberRef) => void;
}) {
  const [tab, setTab] = useState<"why" | "entries">("why");
  const [acting, setActing] = useState(false);
  const [compare, setCompare] = useState<"py" | "pp">(spec.compare === "pp" ? "pp" : "py");
  const [drill, setDrill] = useState<Drill[]>([]);
  const [ex, setEx] = useState<XExplanation | null>(null);
  const [exErr, setExErr] = useState<string | null>(null);
  const [rows, setRows] = useState<XResult | null>(null);
  const [rowsErr, setRowsErr] = useState<string | null>(null);
  const panel = useRef<HTMLElement>(null);

  // A new click starts over.
  useEffect(() => { setDrill([]); setEx(null); setRows(null); setActing(false); }, [tile.id, JSON.stringify(pick)]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { panel.current?.focus(); }, [tile.id]);
  useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", esc); return () => document.removeEventListener("keydown", esc);
  }, [onClose]);

  const filters = drill.map((d) => d.filter);
  const req = useMemo(() => explainRequest(tile, main, spec, pick, filters, compare, !pick && tile.kind === "kpi" ? tile.title : undefined), [tile, main, spec, pick, JSON.stringify(filters), compare]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!req) { setExErr("This tile has no value to explain."); return; }
    let live = true;
    setExErr(null);
    api.xExplain(req).then((r) => { if (live) setEx(r); }).catch((e) => { if (live) setExErr((e as Error).message); });
    return () => { live = false; };
  }, [JSON.stringify(req)]); // eslint-disable-line react-hooks/exhaustive-deps
  const eq = useMemo(() => entriesQuery(tile, main, ds, pick, filters), [tile, main, ds, pick, JSON.stringify(filters)]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (tab !== "entries") return;
    let live = true;
    setRowsErr(null);
    api.xQuery(eq).then((r) => { if (live) setRows(r); }).catch((e) => { if (live) setRowsErr((e as Error).message); });
    return () => { live = false; };
  }, [tab, JSON.stringify(eq)]); // eslint-disable-line react-hooks/exhaustive-deps

  const format = ex?.value.format;
  const good = tile.view?.good !== "down";
  const tone = (v: number | null | undefined) => (v === null || v === undefined || Math.abs(v) < 1e-9 ? "" : (v > 0) === good ? "good" : "bad");
  const title = pick ? pick.label : ex?.value.name ?? tile.title;
  const crumbs = [{ label: pick ? pick.label : "All", i: -1 }, ...drill.map((d, i) => ({ label: `${d.dim}: ${d.label}`, i }))];
  // What the number is, in words: the tile, what was clicked and what was drilled into.
  const what = [tile.title, pick?.label, ...drill.map((d) => d.label)].filter(Boolean).join(" · ");
  const drillInto = (d: XDimension, m: XMember) => {
    const filter: XFilter = m.key === null || m.key === "" ? { column: d.column, op: "empty" } : { column: d.column, op: "in", value: [m.key] };
    setDrill([...drill, { filter, label: m.label, dim: d.name }]);
    setTab("why");
    panel.current?.querySelector(".insp-body")?.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <aside className="insp" ref={panel} tabIndex={-1} aria-label={`Inspect ${tile.title}`}>
      <header className="insp-h">
        <div className="insp-eyebrow"><span>{pick ? `${tile.title} · Explain` : "Explain"}</span><button className="insp-x" aria-label="Close" onClick={onClose}><svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"><path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" /></svg></button></div>
        <h2>{title}</h2>
        {drill.length > 0 && (
          <nav className="insp-crumbs" aria-label="Drilled into">
            {crumbs.map((c, k) => <span key={k}>{k > 0 && <i aria-hidden="true">›</i>}{k < crumbs.length - 1 ? <button className="linkish" onClick={() => setDrill(drill.slice(0, c.i + 1))}>{c.label}</button> : <b>{c.label}</b>}</span>)}
          </nav>
        )}
        <div className="insp-num">
          <div className="insp-v">{ex ? <KpiNumber v={ex.now.value} format={format} /> : <span className="sk sk-v" />}</div>
          {ex?.then && <div className={`insp-d ${tone(ex.change)}`}>{signed(ex.change, format)}{ex.changePct !== null && format !== "pct" ? <span> ({ex.changePct > 0 ? "+" : ""}{(ex.changePct * 100).toFixed(1)}%)</span> : null}<em>vs {plain(ex.then.value, format)}</em></div>}
        </div>
        <div className="insp-meta">
          {ex?.now.from && ex.now.to ? <span>{fmtRange(ex.now.from, ex.now.to)}</span> : <span>All dates</span>}
          {ex?.then?.from && ex.then.to && <span className="muted">against {fmtRange(ex.then.from, ex.then.to)}</span>}
          {tile.date && <div className="seg sm" role="radiogroup" aria-label="Compare to">
            {(["py", "pp"] as const).map((c) => <button key={c} role="radio" aria-checked={compare === c} className={compare === c ? "on" : ""} onClick={() => setCompare(c)}>{c === "py" ? "Prior year" : "Prior period"}</button>)}
          </div>}
        </div>
        <div className="insp-tabs" role="tablist">
          <button role="tab" aria-selected={tab === "why"} className={tab === "why" ? "on" : ""} onClick={() => setTab("why")}>Why</button>
          <button role="tab" aria-selected={tab === "entries"} className={tab === "entries" ? "on" : ""} onClick={() => setTab("entries")}>Entries</button>
        </div>
      </header>

      <div className="insp-body">
        {acting && ex && req ? (
          <ActPanel tile={tile} what={what} ex={ex} req={req} ds={ds} dashboardId={dashboardId} dashboardName={dashboardName}
            onDone={(r) => onActed?.(r)} onCancel={() => setActing(false)} />
        ) : tab === "why" ? (
          exErr ? <div className="dtile-err">{exErr}</div> : !ex ? <WhySkeleton /> : <>
            {actions.length > 0 && (
              <div className="insp-acts" aria-label="Raised from this number">
                <span className="insp-acts-h">{actions.filter((a) => a.open).length ? "Being worked on" : "Raised from here"}</span>
                {actions.slice(0, 4).map((a) => (
                  <Link key={a.objectId} to={recordHref(a.type, a.number)} className="insp-act">
                    <b>{a.number}</b><span className="insp-act-t">{a.recordTitle}</span><StatePill state={a.state} />{a.ownerName && <span className="muted">{a.ownerName}</span>}
                  </Link>
                ))}
              </div>
            )}
            <p className="insp-lede">{ex.headline}</p>
            {ex.detail.length > 0 && <ul className="insp-detail">{ex.detail.map((d, i) => <li key={i}>{d}</li>)}</ul>}
            {ex.bridge && ex.bridge.steps.length > 0 && <Bridge b={ex.bridge} compare={ex.compare ?? compare} good={good} />}
            {ex.dimensions.length === 0 && <p className="muted">No drivers to split this by.</p>}
            {ex.dimensions.map((d) => <Driver key={d.column} d={d} ex={ex} dataset={main.dataset} good={good} onDrill={(m) => drillInto(d, m)} />)}
            <p className="insp-foot muted">Computed from {ds?.name ?? "the dataset"} in {ex.ms < 1000 ? `${ex.ms} ms` : `${(ex.ms / 1000).toFixed(1)} s`}. {ex.kind === "ratio" ? "Each driver's effect is its own rate change plus the shift in its share (mix); together they add up to the change." : ex.kind === "additive" ? "Each driver's members add up to the change exactly." : "Averages don't add up across members, so each is shown on its own."}</p>
          </>
        ) : (
          rowsErr ? <div className="dtile-err">{rowsErr}</div> : !rows ? <WhySkeleton /> : rows.kind === "table" ? <Entries r={rows} dataset={main.dataset} /> : null
        )}
      </div>

      {!acting && (
        <footer className="insp-f">
          {onActed && <button className="btn primary" disabled={!ex || !req} onClick={() => setActing(true)} title="Raise an issue or start an improvement that carries this number">Act on This</button>}
          {pick?.cross && onCross && <button className="btn" onClick={() => onCross(pick.cross!)}>Filter dashboard to {pick.cross.value === null ? "(Blank)" : String(pick.cross.value).slice(0, 28)}</button>}
          {onWorkbook && <button className="btn" onClick={() => onWorkbook(eq, what)}>Open in a workbook</button>}
        </footer>
      )}
    </aside>
  );
}

function WhySkeleton() {
  return <div className="insp-sk" aria-label="Loading">{[92, 70, 84, 40, 76, 60].map((w, i) => <span key={i} className="sk" style={{ width: `${w}%` }} />)}</div>;
}

/** One driver: its members, as bars that diverge from the middle by how much each moved the number. */
function Driver({ d, ex, dataset, good, onDrill }: { d: XDimension; ex: XExplanation; dataset: string; good: boolean; onDrill: (m: XMember) => void }) {
  const [open, setOpen] = useState(false);
  const format = ex.value.format;
  const compared = !!ex.then;
  const list = open ? d.members : d.members.slice(0, 5);
  const max = Math.max(...d.members.map((m) => Math.abs(m.change)), Math.abs(d.others?.change ?? 0), 1e-12);
  const twoSided = compared && d.members.some((m) => m.change < 0) && d.members.some((m) => m.change > 0);
  const lead = d.members[0];
  const share = compared && ex.change ? Math.abs(lead.change / ex.change) : null;
  return (
    <section className="drv">
      <header className="drv-h">
        <h3>By {d.name.toLowerCase().replace(/^g\/l/, "G/L")}</h3>
        <span className="muted">{d.count} {d.count === 1 ? "value" : "values"}{share !== null && Math.sign(lead.change) === Math.sign(ex.change ?? 0) ? ` · top one carries ${share > 1.05 ? "more than all" : `${Math.round(Math.min(share, 1) * 100)}%`}` : ""}</span>
      </header>
      <div className={`drv-list ${twoSided ? "two" : ""}`} role="list">
        {list.map((m, i) => {
          const w = (Math.abs(m.change) / max) * (twoSided ? 50 : 100);
          const link = memberLink(dataset, d.column, m.key);
          const tone = !compared ? "neutral" : m.change === 0 ? "neutral" : (m.change > 0) === good ? "good" : "bad";
          return (
            <div key={i} className="drv-r" role="listitem">
              <button className="drv-name" onClick={() => onDrill(m)} title={`Why did ${m.label} change?`}>
                <span className="drv-l">{m.label}</span>
                {m.code && <span className="drv-code">{m.code}</span>}
                {m.status === "new" && <span className="drv-tag">New</span>}
                {m.status === "gone" && <span className="drv-tag gone">None now</span>}
              </button>
              <span className="drv-bar" aria-hidden="true"><i className={tone} style={{ width: `${Math.max(0.6, w)}%`, ...(twoSided ? (m.change < 0 ? { right: "50%" } : { left: "50%" }) : { left: 0 }) }} /></span>
              <span className={`drv-c ${compared ? tone : ""}`}>{compared ? signed(m.change, format) : plain(m.change, format)}</span>
              <span className="drv-sub">
                {compared ? <>{plain(m.then, format)} → {plain(m.now, format)}{m.rate !== undefined && m.mix !== undefined && (Math.abs(m.rate) > 1e-6 || Math.abs(m.mix) > 1e-6) ? <> · rate {signed(m.rate, format)}, mix {signed(m.mix, format)}</> : null}</> : m.nowShare !== undefined ? <>{plain(m.now, format)} · {Math.round(m.nowShare * 100)}% of the base</> : null}
                {link && (link.external ? <a href={link.href} target="_blank" rel="noopener noreferrer" title={link.title} className="drv-out">BC ↗</a> : <Link to={link.href} title={link.title} className="drv-out">Open</Link>)}
              </span>
            </div>
          );
        })}
        {!open && d.members.length > 5 && <button className="linkish drv-more" onClick={() => setOpen(true)}>Show {d.members.length - 5} more</button>}
        {open && d.others && <div className="drv-r other"><span className="drv-name"><span className="drv-l">{d.others.count} others</span></span><span /><span className="drv-c">{compared ? signed(d.others.change, format) : plain(d.others.change, format)}</span></div>}
      </div>
    </section>
  );
}

/** Price, volume and mix: a floating waterfall from the comparison to now. Start and end are marks, not bars from zero. */
function Bridge({ b, compare, good = true }: { b: NonNullable<XExplanation["bridge"]>; compare: "py" | "pp"; good?: boolean }) {
  let run = b.start;
  const steps = b.steps.map((s) => { const from = run; run += s.value; return { ...s, from, to: run }; });
  const all = [b.start, b.end, ...steps.flatMap((s) => [s.from, s.to])];
  const lo = Math.min(...all), hi = Math.max(...all), pad = (hi - lo) * 0.08 || Math.abs(hi) * 0.05 || 1;
  const X = (v: number) => ((v - (lo - pad)) / (hi - lo + 2 * pad)) * 100;
  const row = (label: string, value: string, el: ReactNode, cls = "") => <div className={`br-r ${cls}`}><span className="br-l">{label}</span><span className="br-t">{el}</span><span className="br-v">{value}</span></div>;
  return (
    <section className="bridge">
      <header className="drv-h"><h3>{b.kind === "margin" ? "Margin bridge" : b.kind === "spend" ? "Spend bridge" : "Revenue bridge"}</h3><span className="muted">item by item, {CMP[compare]} to now</span></header>
      {row(CMP[compare][0].toUpperCase() + CMP[compare].slice(1), plain(b.start, "money"), <i className="br-mark" style={{ left: `${X(b.start)}%` }} />, "end")}
      {steps.map((s) => row(s.label, signed(s.value, "money"), <i className={`br-bar ${(s.value >= 0) === good ? "up" : "down"}`} style={{ left: `${X(Math.min(s.from, s.to))}%`, width: `${Math.max(0.6, X(Math.max(s.from, s.to)) - X(Math.min(s.from, s.to)))}%` }} />))}
      {row("This period", plain(b.end, "money"), <i className="br-mark now" style={{ left: `${X(b.end)}%` }} />, "end")}
      <p className="muted br-note">{b.kind === "margin" ? "Volume: more or fewer units at last period's margins. Price and cost: what each item sold and cost for. Mix: selling more of the higher-margin items."
        : b.kind === "spend" ? "Quantity: buying more or less of each item, at last period's cost. Purchase price: what each item cost to buy against last period (purchase price variance)."
        : "Volume: more or fewer units at last period's prices. Price: what each item sold for. Mix: selling more of the higher-priced items."}</p>
    </section>
  );
}

/** The ledger rows behind the number, with links into BC and courageMFG. */
function Entries({ r, dataset }: { r: Extract<XResult, { kind: "table" }>; dataset: string }) {
  if (!r.rows.length) return <p className="muted">No entries.</p>;
  const ids = r.columns.map((c) => c.id);
  return (
    <>
      <p className="insp-count">{r.total > r.rows.length ? `The latest ${r.rows.length.toLocaleString("en-US")} of ${r.total.toLocaleString("en-US")} entries` : `${r.total.toLocaleString("en-US")} ${r.total === 1 ? "entry" : "entries"}`}</p>
      <div className="dt-wrap insp-dt"><table className="dt">
        <thead><tr>{r.columns.map((c) => <th key={c.id} className={c.type === "number" ? "r" : ""}>{c.name}</th>)}</tr></thead>
        <tbody>{r.rows.map((row, i) => {
          const byId = Object.fromEntries(ids.map((id, j) => [id, row[j]]));
          return <tr key={i}>{row.map((v, j) => {
            const c = r.columns[j], link = cellLink(dataset, c.id, v, byId), text = fmt(v, c);
            return <td key={j} className={c.type === "number" ? "r num" : ""}>{link ? (link.external ? <a href={link.href} target="_blank" rel="noopener noreferrer" title={link.title}>{text}</a> : <Link to={link.href} title={link.title}>{text}</Link>) : text}</td>;
          })}</tr>;
        })}</tbody>
        {r.totals.some((t) => t !== null) && <tfoot><tr>{r.totals.map((t, j) => <td key={j} className={r.columns[j].type === "number" ? "r num" : ""}>{j === 0 ? "Total" : t === null ? "" : fmt(t, r.columns[j])}</td>)}</tr></tfoot>}
      </table></div>
    </>
  );
}
